import React from 'react';
import { Box, Typography, TextLink, Chip } from '@ellucian/react-design-system/core';
import { Icon } from '@ellucian/ds-icons/lib';

interface Documentation {
  label: string;
  url: string;
}

interface SolutionSidebarProps {
  createdBy: string;
  tags: string[];
  type?: string;
  license: string;
  documentation: Documentation[];
  isMobile?: boolean;
  isTablet?: boolean;
}

const SolutionSidebar: React.FC<SolutionSidebarProps> = ({
  createdBy,
  tags,
  type,
  license,
  documentation,
  isMobile = false,
  isTablet = false
}) => {
  return (
  <Box sx={{ 
    display: 'flex', 
    width: isMobile ? '100%' : (isTablet ? 280 : 300), 
    flexDirection: 'column', 
    alignItems: 'flex-start', 
    gap: isMobile ? '20px' : (isTablet ? '24px' : '32px'),
    ...(isMobile && { 
      flexDirection: 'row',
      flexWrap: 'wrap',
      '& > *': { 
        flex: isMobile ? '1 1 calc(50% - 10px)' : 'none',
        minWidth: isMobile ? 'calc(50% - 10px)' : 'auto'
      }
    })
  }}>
    {/* Created By */}
  <Box sx={{ 
    pt: isMobile ? 0 : (isTablet ? 5 : 6),
    width: isMobile ? 'auto' : '100%'
  }}>
  <Typography variant="body2" sx={{ alignSelf: 'stretch', fontWeight: 700, mb: 1, fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto, "Century Gothic", Futura, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif' }}>
        Created By
      </Typography>
      <Typography
        variant="body2"
        sx={{
          alignSelf: 'stretch',
          color: 'var(--Neutral-neutral-600, #151618)',
          fontWeight: 400,
          fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto, "Century Gothic", Futura, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif'
        }}
      >
  {createdBy.trim()}
      </Typography>
    </Box>

  {/* Tags */}
  <Box sx={{ 
    width: isMobile ? 'auto' : '100%',
    minWidth: 0,
    overflow: 'visible'
  }}>
  <Typography variant="body2" sx={{ alignSelf: 'stretch', fontWeight: 700, mb: 1, fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto, "Century Gothic", Futura, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif' }}>
          Tags
        </Typography>
      <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {(tags || []).map((tag, idx) => (
          <Chip
            key={idx}
            label={tag}
            sx={{
              border: '1px solid var(--Neutral-neutral-400, #B2B3B7)',
              borderRadius: '20px',
              backgroundColor: '#FFF',
              flexShrink: 0,
              height: 'auto',
              maxWidth: '100%',
              '& .MuiChip-label': {
                whiteSpace: 'normal',
                padding: '4px 12px',
                color: 'var(--Neutral-neutral-600, #151618)',
                fontWeight: 400,
                fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto, "Century Gothic", Futura, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word'
              }
            }}
          />
        ))}
      </Box>
    </Box>

  {/* Type */}
  {type && type.trim() && (
  <Box sx={{ 
    width: isMobile ? 'auto' : '100%' 
  }}>
  <Typography variant="body2" sx={{ alignSelf: 'stretch', fontWeight: 700, mb: 1, fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto, "Century Gothic", Futura, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif' }}>
          Type
        </Typography>
        <Typography
          variant="body2"
          sx={{
            alignSelf: 'stretch',
            color: 'var(--Neutral-neutral-600, #151618)',
            fontWeight: 400,
            fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto, "Century Gothic", Futura, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif'
          }}
        >
          {type.trim()}
        </Typography>
    </Box>
  )}

  {/* Documentation and Resources */}
  {documentation && documentation.length > 0 && (
  <Box sx={{ 
    width: isMobile ? '100%' : '100%' 
  }}>
        <Typography
          variant="body2"
          sx={{
            alignSelf: 'stretch',
            fontWeight: 700,
            color: 'var(--Neutral-neutral-600, #151618)',
            mb: 1,
            fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto, "Century Gothic", Futura, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif'
          }}
        >
          Documentation and Resources
        </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', alignSelf: 'stretch' }}>
        {(documentation || []).map((doc) => (
          <Box key={doc.label} sx={{ display: 'flex', alignItems: 'center', gap: '8px', alignSelf: 'stretch' }}>
            <Icon name="link" style={{ width: 16, height: 16, color: 'var(--Interactive-Blue-cta-blue-600, #026BC8)' }} />
            <TextLink
              href={doc.url}
              target="_blank"
              rel="noopener noreferrer"
              underline="always"
              id={`doc-link-${doc.label.replace(/\s+/g, '-').toLowerCase()}`}
              className="doc-link-override"
              sx={{
                color: 'var(--Interactive-Blue-cta-blue-600, #026BC8)',
                textDecorationLine: 'underline',
                textDecorationStyle: 'solid',
                textDecorationSkipInk: 'none',
                textDecorationThickness: 'auto',
                textUnderlineOffset: 'auto',
                textUnderlinePosition: 'from-font',
              }}
            >
              {doc.label}
            </TextLink>
          </Box>
        ))}
      </Box>
    </Box>
  )}
  </Box>
);
};

export default SolutionSidebar;
